import type { EventMarketDto } from '@/api/queries';
import type { CashoutSettings } from '@/api/auth';
import {
  MIN_CASHOUT_OFFER,
  applyLiveOddsToCashoutTicket,
  offerFromCashoutListTicket,
  type CashoutHistoryEntry,
  type CashoutTicketItem,
  type OpenCashoutTicket,
} from '@/domain/cashout';

type Listener = () => void;

export type CashoutOfferState = {
  offer: number;
  /** True while a live recalc briefly flashes Pending in the UI. */
  updating: boolean;
};

type CashoutStoreState = {
  tickets: Map<string, OpenCashoutTicket>;
  offers: Map<string, CashoutOfferState>;
  history: Map<string, CashoutHistoryEntry[]>;
  settings: CashoutSettings | null;
};

let state: CashoutStoreState = {
  tickets: new Map(),
  offers: new Map(),
  history: new Map(),
  settings: null,
};
let version = 0;
const listeners = new Set<Listener>();

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribeCashout(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCashoutVersion(): number {
  return version;
}

export function getCashoutSnapshot(): number {
  return version;
}

export function getOpenCashoutTickets(): OpenCashoutTicket[] {
  return [...state.tickets.values()];
}

export function getCashoutOffer(ticketId: string): CashoutOfferState | undefined {
  return state.offers.get(ticketId);
}

export function getCashoutHistory(ticketId: string): CashoutHistoryEntry[] {
  return state.history.get(ticketId) ?? [];
}

export function hasOpenCashoutTickets(): boolean {
  return state.tickets.size > 0;
}

function settingsEqual(
  a: CashoutSettings | null,
  b: CashoutSettings | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.AllowCashout === b.AllowCashout &&
    a.AllowCashoutBonus === b.AllowCashoutBonus &&
    a.AutoCashout === b.AutoCashout &&
    a.PartialCashout === b.PartialCashout &&
    a.CashoutMaximumOdds === b.CashoutMaximumOdds &&
    a.CashoutMode === b.CashoutMode &&
    a.CashoutSpeedLevel === b.CashoutSpeedLevel &&
    a.CashoutProfitPercent === b.CashoutProfitPercent
  );
}

export function setCashoutSettings(settings: CashoutSettings | null | undefined): void {
  const next = settings ?? null;
  if (settingsEqual(state.settings, next)) return;
  state = { ...state, settings: next };
  emit();
}

/** Replace open tickets from the current My Bets list (eligible only). */
export function setOpenCashoutTickets(tickets: OpenCashoutTicket[]): void {
  const nextIds = tickets.map((ticket) => ticket.Id).join('|');
  const prevIds = [...state.tickets.keys()].join('|');
  if (nextIds === prevIds) {
    // Same open set — refresh stake/win from the list without wiping priced Items
    // (cashoutlist / socket own those) or dropping offers/history.
    const nextTickets = new Map(state.tickets);
    for (const ticket of tickets) {
      const previous = nextTickets.get(ticket.Id);
      if (!previous) {
        nextTickets.set(ticket.Id, ticket);
        continue;
      }
      nextTickets.set(ticket.Id, {
        ...previous,
        Stake: ticket.Stake,
        PossibleWin: ticket.PossibleWin,
        Odds: ticket.Odds,
        Probability: ticket.Probability ?? previous.Probability,
        CashoutWin: ticket.CashoutWin,
        InitialStake: ticket.InitialStake ?? previous.InitialStake,
        Live: ticket.Live,
      });
    }
    state = { ...state, tickets: nextTickets };
    // No emit: offer values are owned by cashoutlist / socket paths.
    return;
  }

  const nextTickets = new Map<string, OpenCashoutTicket>();
  const nextOffers = new Map<string, CashoutOfferState>();
  const nextHistory = new Map<string, CashoutHistoryEntry[]>();

  for (const ticket of tickets) {
    nextTickets.set(ticket.Id, ticket);
    const previous = state.offers.get(ticket.Id);
    nextOffers.set(ticket.Id, previous ?? { offer: 0, updating: false });
    const hist = state.history.get(ticket.Id);
    if (hist?.length) nextHistory.set(ticket.Id, hist);
  }

  state = {
    ...state,
    tickets: nextTickets,
    offers: nextOffers,
    history: nextHistory,
  };
  emit();
}

export function applyCashoutListResponse(
  response: OpenCashoutTicket[] | null | undefined,
): void {
  if (!Array.isArray(response)) return;
  const settings = state.settings;
  const nextTickets = new Map(state.tickets);
  const nextOffers = new Map(state.offers);

  for (const row of response) {
    const id = String(row.Id ?? '');
    if (!id) continue;
    const existing = nextTickets.get(id);
    if (!existing) continue;
    const merged: OpenCashoutTicket = {
      ...existing,
      Items: (row.Items as CashoutTicketItem[] | undefined) ?? existing.Items,
    };
    const offer = offerFromCashoutListTicket(merged, merged.Items, settings);
    nextTickets.set(id, merged);
    nextOffers.set(id, { offer, updating: false });
  }

  state = { ...state, tickets: nextTickets, offers: nextOffers };
  emit();
}

export function applyCashoutOddsPatch(
  eventId: number,
  markets: EventMarketDto[] | undefined,
  active: boolean | undefined,
): void {
  const settings = state.settings;
  let changed = false;
  const nextTickets = new Map(state.tickets);
  const nextOffers = new Map(state.offers);

  for (const [id, ticket] of state.tickets) {
    if (!ticket.Items.some((item) => item.Event.EventId === eventId)) continue;
    const clone: OpenCashoutTicket = {
      ...ticket,
      Items: ticket.Items.map((item) => ({
        Event: { ...item.Event },
        Bets: item.Bets.map((bet) => ({ ...bet })),
      })),
    };
    const offer = applyLiveOddsToCashoutTicket(clone, eventId, markets, active, settings);
    const prev = state.offers.get(id)?.offer ?? 0;
    const oddsChanged = ticketOddsSignature(ticket) !== ticketOddsSignature(clone);
    if (!oddsChanged && offer === prev) continue;
    nextTickets.set(id, clone);
    nextOffers.set(id, {
      offer,
      updating: offer !== prev && (offer >= MIN_CASHOUT_OFFER || prev >= MIN_CASHOUT_OFFER),
    });
    changed = true;
  }

  if (!changed) return;
  state = { ...state, tickets: nextTickets, offers: nextOffers };
  emit();
}

function ticketOddsSignature(ticket: OpenCashoutTicket): string {
  return ticket.Items.map((item) =>
    item.Bets.map((bet) => `${bet.Odd ?? ''}:${bet.Probability ?? ''}`).join(','),
  ).join('|');
}

/** Clear the brief "Updating…" flag after the UI has shown it. */
export function clearCashoutUpdating(ticketId: string): void {
  const current = state.offers.get(ticketId);
  if (!current?.updating) return;
  const nextOffers = new Map(state.offers);
  nextOffers.set(ticketId, { ...current, updating: false });
  state = { ...state, offers: nextOffers };
  emit();
}

export function setCashoutOfferAmount(ticketId: string, offer: number): void {
  const nextOffers = new Map(state.offers);
  nextOffers.set(ticketId, { offer, updating: false });
  state = { ...state, offers: nextOffers };
  emit();
}

export function applySuccessfulFullCashout(ticketId: string): void {
  const nextTickets = new Map(state.tickets);
  const nextOffers = new Map(state.offers);
  const nextHistory = new Map(state.history);
  nextTickets.delete(ticketId);
  nextOffers.delete(ticketId);
  nextHistory.delete(ticketId);
  state = {
    ...state,
    tickets: nextTickets,
    offers: nextOffers,
    history: nextHistory,
  };
  emit();
}

export function applySuccessfulPartialCashout(
  ticketId: string,
  percentage: number,
  amount: number,
): void {
  const ticket = state.tickets.get(ticketId);
  if (!ticket) return;

  const factor = 1 - percentage / 100;
  const nextTicket: OpenCashoutTicket = {
    ...ticket,
    Stake: ticket.Stake * factor,
    PossibleWin: ticket.PossibleWin * factor,
  };
  const prevOffer = state.offers.get(ticketId)?.offer ?? 0;
  const nextOffer = Math.max(0, Math.floor((prevOffer - amount + Number.EPSILON) * 100) / 100);

  const nextTickets = new Map(state.tickets);
  const nextOffers = new Map(state.offers);
  const nextHistory = new Map(state.history);
  nextTickets.set(ticketId, nextTicket);
  nextOffers.set(ticketId, { offer: nextOffer, updating: false });
  const stakeUsed =
    Math.floor(((ticket.Stake * (percentage / 100) + Number.EPSILON) * 100)) / 100;
  nextHistory.set(ticketId, [
    ...(state.history.get(ticketId) ?? []),
    { percentage, stake: stakeUsed, amount, at: Date.now() },
  ]);

  state = {
    ...state,
    tickets: nextTickets,
    offers: nextOffers,
    history: nextHistory,
  };
  emit();
}

/** Drop optimistic session history once the ticket list carries PartialCashoutHistory. */
export function clearCashoutHistory(ticketId: string): void {
  if (!state.history.has(ticketId)) return;
  const nextHistory = new Map(state.history);
  nextHistory.delete(ticketId);
  state = { ...state, history: nextHistory };
  emit();
}

export function clearCashoutStore(): void {
  if (state.tickets.size === 0 && state.offers.size === 0 && state.history.size === 0) {
    return;
  }
  state = {
    tickets: new Map(),
    offers: new Map(),
    history: new Map(),
    settings: state.settings,
  };
  emit();
}
